import React, {
    ReactElement,
    ReactNode,
    createContext,
    useCallback,
    useMemo,
    useState,
} from "react"

import { LCTOnlyDatasets } from "../../../../../../constants/enums"
import { AppError } from "../../../../../../errors/errorProcessors"
import usePublishLCTDevice from "../../../../../../services/middleware/hooks/devices/lct/usePublishLCTDevice"
import useSaveLCTDeviceDraft from "../../../../../../services/middleware/hooks/devices/lct/useSaveLCTDeviceDraft"
import { checkContextErrors } from "../../../../../../utils/registerDevice/registerDeviceUtils"
import { IDeviceFormContextValue } from "../../../../types/providerTypes"
import { EVACDeviceFormData } from "../../../types/providerTypes"

type CreateEVACDeviceProviderProps = {
    formData: EVACDeviceFormData
    setFormData: React.Dispatch<React.SetStateAction<EVACDeviceFormData>>
    formDataIsLoading: boolean
    formDataError?: AppError
    systemReference?: string
    attachmentUUID?: string
    canEditManufacturer: boolean
    isNewVersion?: boolean
    children: ReactNode
}

export const CreateEVACDeviceContext = createContext(
    {} as IDeviceFormContextValue<EVACDeviceFormData>
)

const CreateEVACDeviceProvider = ({
    formData,
    setFormData,
    formDataIsLoading,
    formDataError,
    systemReference,
    attachmentUUID,
    canEditManufacturer,
    isNewVersion = false,
    children,
}: CreateEVACDeviceProviderProps): ReactElement => {
    const [currentIndex, setCurrentIndex] = useState<number>(0)
    const [deviceRef, setDeviceRef] = useState<string | undefined>()
    const [errors, setErrors] = useState<AppError[]>([])

    const {
        publishLCTDevice,
        error: publishError,
        isLoaded: publishSuccessful,
        isLoading: publishInProgress,
    } = usePublishLCTDevice(
        formData,
        LCTOnlyDatasets.EVAC,
        attachmentUUID,
        systemReference
    )

    const {
        saveLCTDeviceDraft,
        isLoading: saveDraftInProgress,
        isLoaded: saveDraftSuccessful,
    } = useSaveLCTDeviceDraft<EVACDeviceFormData>(
        false,
        LCTOnlyDatasets.EVAC,
        systemReference,
        attachmentUUID
    )

    checkContextErrors([formDataError, publishError], errors, (error) =>
        setErrors((prev) => [...prev, error])
    )
    const setIndex = (step: number): void => {
        setErrors([])
        setCurrentIndex(step)
    }

    const onSubmit = useCallback(async () => {
        const res = await publishLCTDevice()
        setDeviceRef(res?.deviceRef)
    }, [publishLCTDevice, setDeviceRef])

    const value: IDeviceFormContextValue<EVACDeviceFormData> = useMemo(
        () => ({
            formData,
            setFormData,
            currentIndex,
            setCurrentIndex: setIndex,
            onSubmit,
            submitLoading: publishInProgress,
            submitSuccessful: publishSuccessful,
            canEditManufacturer,
            isNewVersion,
            formIsLoading: formDataIsLoading,
            saveDraft: saveLCTDeviceDraft,
            saveDraftLoading: saveDraftInProgress,
            saveDraftSuccessful,
            deviceRef,
            attachmentUUID,
            errors,
        }),
        [
            formData,
            setFormData,
            currentIndex,
            onSubmit,
            publishInProgress,
            publishSuccessful,
            canEditManufacturer,
            isNewVersion,
            formDataIsLoading,
            saveLCTDeviceDraft,
            saveDraftInProgress,
            saveDraftSuccessful,
            deviceRef,
            attachmentUUID,
            errors,
        ]
    )

    return (
        <CreateEVACDeviceContext.Provider value={value}>
            {children}
        </CreateEVACDeviceContext.Provider>
    )
}

CreateEVACDeviceProvider.defaultProps = {
    formDataError: undefined,
    systemReference: undefined,
    attachmentUUID: undefined,
}

export default CreateEVACDeviceProvider
